import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Play, Power } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SortableHeader } from '@/components/SortableTable';
import { Badge, Button, Card, CardContent } from '@/components/ui';
import { inferenceApi, type InferenceRule } from '@/lib/api';
import { useTableState } from '@/lib/use-table-state';

type RuleColumn = 'name' | 'scope' | 'triplet_count' | 'last_run_at' | 'status';

const RULE_COLUMNS = [
  { key: 'name' as const, accessor: (r: InferenceRule) => r.name.toLowerCase() },
  { key: 'scope' as const, accessor: (r: InferenceRule) => r.scope ?? '' },
  { key: 'triplet_count' as const, accessor: (r: InferenceRule) => r.triplet_count },
  {
    key: 'last_run_at' as const,
    accessor: (r: InferenceRule) => r.last_run_at ?? '',
  },
  { key: 'status' as const, accessor: (r: InferenceRule) => r.status },
];

export function InferenceRuleLibrary() {
  const { t, i18n } = useTranslation(['studio', 'common']);
  const qc = useQueryClient();
  const [runMessage, setRunMessage] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['studio', 'inference', 'rules'],
    queryFn: () => inferenceApi.listRules(),
  });

  const tableState = useTableState<InferenceRule, RuleColumn>({
    rows: data ?? [],
    columns: RULE_COLUMNS,
    initialSort: { by: 'name', dir: 'asc' },
  });

  const toggle = useMutation({
    mutationFn: ({ ruleId, enabled }: { ruleId: string; enabled: boolean }) =>
      inferenceApi.patchRule(ruleId, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['studio', 'inference', 'rules'] }),
  });

  const run = useMutation({
    mutationFn: (ruleId: string) => inferenceApi.runRule(ruleId),
    onSuccess: (result) => {
      setRunMessage(t('studio:inference.rules.runCompleted', { count: result.triple_count }));
      qc.invalidateQueries({ queryKey: ['studio', 'inference', 'rules'] });
    },
  });

  const sortable = (key: RuleColumn) => ({
    active: tableState.sort?.by === key,
    direction: tableState.sort?.dir,
    onClick: () => tableState.toggleSort(key),
  });

  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-h2 text-brand-900">{t('studio:inference.rules.title')}</h2>
        <p className="mt-1 text-small text-slate-600">{t('studio:inference.rules.subtitle')}</p>
      </header>

      {runMessage && (
        <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-small text-green-800">
          {runMessage}
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading && (
            <p className="px-4 py-3 text-small text-slate-500">{t('common:status.loading')}</p>
          )}
          {!isLoading && data && data.length === 0 && (
            <p className="px-4 py-6 text-center text-body text-slate-500">
              {t('studio:inference.rules.noRules')}
            </p>
          )}
          {data && data.length > 0 && (
            <table className="w-full text-body">
              <thead className="border-b border-slate-200 bg-slate-50 text-h4 text-slate-600">
                <tr>
                  <SortableHeader
                    label={t('studio:inference.rules.columns.name')}
                    {...sortable('name')}
                  />
                  <SortableHeader
                    label={t('studio:inference.rules.columns.scope')}
                    {...sortable('scope')}
                  />
                  <SortableHeader
                    label={t('studio:inference.rules.columns.tripletCount')}
                    {...sortable('triplet_count')}
                  />
                  <SortableHeader
                    label={t('studio:inference.rules.columns.lastRun')}
                    {...sortable('last_run_at')}
                  />
                  <SortableHeader
                    label={t('studio:inference.rules.columns.status')}
                    {...sortable('status')}
                  />
                  <th className="px-3 py-2 text-right text-h4 text-slate-600">
                    {t('studio:inference.rules.columns.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tableState.page.map((rule) => (
                  <tr key={rule.rule_id} className="hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <p className="font-medium text-brand-900">{rule.name}</p>
                      <p className="text-small text-slate-500">{rule.description}</p>
                    </td>
                    <td className="px-3 py-2 font-mono text-small text-slate-700">
                      {rule.scope ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-small text-slate-900">{rule.triplet_count}</td>
                    <td className="px-3 py-2 text-small text-slate-500">
                      {rule.last_run_at
                        ? new Date(rule.last_run_at).toLocaleString(i18n.language)
                        : t('studio:inference.rules.neverRun')}
                    </td>
                    <td className="px-3 py-2">
                      <Badge
                        variant={
                          rule.status === 'active' && rule.enabled
                            ? 'success'
                            : rule.status === 'deprecated'
                              ? 'warning'
                              : 'neutral'
                        }
                      >
                        {t(`studio:inference.rules.status.${rule.status}`)}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            toggle.mutate({ ruleId: rule.rule_id, enabled: !rule.enabled })
                          }
                          aria-label={t('studio:inference.rules.actions.toggle')}
                          title={t('studio:inference.rules.actions.toggle')}
                          disabled={toggle.isPending}
                        >
                          <Power
                            className={rule.enabled ? 'h-4 w-4 text-green-600' : 'h-4 w-4 text-slate-400'}
                            aria-hidden
                          />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => run.mutate(rule.rule_id)}
                          aria-label={t('studio:inference.rules.actions.run')}
                          title={t('studio:inference.rules.actions.run')}
                          disabled={run.isPending || !rule.enabled}
                        >
                          <Play className="h-4 w-4 text-accent" aria-hidden />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
